<template>
  <section class="page-merge-request-overview">
    <div class="page-merge-request-overview__content">
      <gf-custom-alert
        v-if="hasNeedToRebaseAlert"
        class="page-merge-request-overview__alert"
        variant="danger"
      >
        <template #body>
          <div class="rebease-alert">
            <span>{{ $t('sourceBranchNeedToRebaseDescription') }}</span>
            <gf-button
              variant="danger"
              :text="$t('sourceBranchNeedToRebase')"
              @click="rebaseSourceBranch"
            />
          </div>
        </template>
      </gf-custom-alert>
      <gf-custom-alert
        v-else-if="hasNeedToRebaseLocalAlert"
        class="page-merge-request-overview__alert"
        variant="danger"
      >
        <template #body>
          <div class="rebease-local-alert">
            {{ $t('sourceBranchNeedToRebaseLocalDescription') }}
          </div>
        </template>
      </gf-custom-alert>
      <merge-request-approve-rules
        v-if="isMrApproversEnabled && areMergeRequestApproversModulesLoaded"
        @approveAction="onApproveAction"
      />
      <div class="page-merge-request-overview__description">
        <h6>{{ $t('request_description') }}</h6>
        <div class="description markdown-body">
          <div v-html="mergeRequest.description || $t('message.description_empty')"></div>
        </div>
      </div>
      <merge-request-auto-merge-card v-if="isMergeRequestAutoMergeShown" />
      <merge-pipeline-card v-if="isMergeRequestPipelineShown" />
      <merge-result-pipeline-card v-if="isMergeRequestResultPipelineShown" />
      <merge-request-merge-area-card
        v-if="isMergeRequestMergeAreaShown"
        @cancel="cancelMergeRequest"
        @close="closeMergeRequest"
      />
      <div
        class="page-merge-request-overview__discussions"
        id="discussions"
        ref="discussions"
      >
        <div class="title">
          <h5>{{ $t('discussions') }}</h5>
          <div class="actions">
            <gf-button
              v-if="getActionAvailabilityFlag(ACTION_ENUM.CREATE_DISCUSSION) && !isNewDiscussionCreating"
              variant="primary"
              :text="$t('message.create')"
              @click="onCreateDiscussionClick"
            />
          </div>
        </div>
        <div class="content">
          <merge-request-discussion-reply-form
            v-if="isNewDiscussionCreating"
            ref="createDiscussionForm"
            @close="onNewDiscussionFormClose"
            @submit="onNewDiscussionSubmit"
          />
          <merge-request-discussions v-if="getActionAvailabilityFlag(ACTION_ENUM.READ_DISCUSSION)" />
        </div>
      </div>
    </div>
    <div class="page-merge-request-overview__aside">
      <merge-request-approve-form
        v-if="areMergeRequestApproversEnabled && areMergeRequestApproversModulesLoaded"
        @update="getMergeRequest"
      />
      <div
        v-if="mergeRequest.reasonWorkInProgress"
        class="form --locked"
      >
        <b class="title">
          <i class="lock-icon gf-icon gf-icon-lock"></i>
          <span>
            {{ $t('message.blocked_due_to') }}
          </span>
        </b>
        <div class="content">
          <small class="muted">{{ mergeRequest?.reasonWorkInProgress }}</small>
        </div>
      </div>
      <div
        v-if="mergeRequest.assignedUsers?.length > 0 || hasEditAccess"
        class="form --assigned"
      >
        <b class="title">{{ $tc('message.approvers', 1) }}</b>
        <div
          v-if="mergeRequest.assignedUsers?.length > 0"
          class="content"
        >
          <user-label-item
            v-for="user in mergeRequest.assignedUsers"
            :key="user.id"
            :user="user"
            :has-user-link="true"
            :open-in-new-tab="false"
            image-size="38"
          />
        </div>
        <div
          v-else-if="hasEditAccess"
          class="content"
        >
          <small class="muted">{{ $t('assign_assigners') }}</small>
        </div>
      </div>

      <div
        v-if="mergeRequest.reviewers?.length > 0 || hasEditAccess"
        class="form --reviewers"
      >
        <b class="title">{{ $tc('message.reviewers', 1) }}</b>
        <div
          v-if="mergeRequest.reviewers?.length > 0"
          class="content"
        >
          <user-label-item
            v-for="user in mergeRequest.reviewers"
            :key="user.id"
            :user="user"
            :has-user-link="true"
            :open-in-new-tab="false"
            image-size="38"
          />
        </div>
        <div
          v-else
          class="content"
        >
          <small class="muted">{{ $t('select_reviewers') }}</small>
        </div>
      </div>

      <div
        v-if="mergeRequest?.labels?.length > 0 || hasEditAccess"
        class="form --labels"
      >
        <b class="title">Лейблы</b>
        <div
          v-if="mergeRequest.labels?.length > 0"
          class="content"
        >
          <repo-label-list :labels="mergeRequest.labels" />
        </div>
        <div
          v-else
          class="content"
        >
          <small class="muted">{{ $t('add_labels') }}</small>
        </div>
      </div>

      <merge-request-security-card v-if="areSecurityModulesLoaded" />

      <div
        v-if="mergeRequest.assignedIssues?.length > 0 || hasEditAccess"
        class="form --issues"
      >
        <b
          v-if="mergeRequest.assignedIssues?.length > 0 || hasEditAccess"
          class="title"
        >
          {{ $t('linked_issues') }}
        </b>
        <div
          v-if="mergeRequest.assignedIssues?.length > 0"
          class="content --short-gap"
        >
          <issue-card-short
            v-for="issue in mergeRequest.assignedIssues"
            :key="issue.id"
            :issue="issue"
          />
        </div>
        <div
          v-else-if="hasEditAccess"
          class="content"
        >
          <small class="muted">{{ $t('link_issue') }}</small>
        </div>
      </div>

      <div
        v-if="mergeRequest?.updatedAt || mergeRequest?.createdAt"
        class="form --created"
      >
        <div class="content">
          <span
            v-if="mergeRequest?.updatedAt"
            class="updated-at"
          >
            <i class="gf-icon gf-icon-clock gf-fs-2 muted"></i>
            <small>
              {{ $tc('message.update', 2) }}
              {{ $moment(mergeRequest?.updatedAt).utc().format('DD.MM.YY [в] HH:mm') }}
            </small>
          </span>
          <span
            v-if="mergeRequest?.createdAt"
            class="created-at"
          >
            <i class="gf-icon gf-icon-calendar gf-fs-2 muted"></i>
            <small>
              {{ $tc('message.created', 1).toLowerCase() }}
              {{ $moment(mergeRequest?.createdAt).utc().format('DD.MM.YY [в] HH:mm') }}
            </small>
          </span>
        </div>
      </div>
      <div class="form --more">
        <div class="about">
          <a
            href="https://docs.gitflic.ru/project/merge_request"
            target="_blank"
          >
            {{ $t('about') }}
          </a>
          <span class="about-icon">
            <i class="gf-icon gf-icon-arrow-outward gf-fs-2"></i>
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Vue from 'vue';
import MergeRequestService from '@shared/modules/merge-request/merge-request.service';
// eslint-disable-next-line max-len
import MergeResultPipelineCard from '@shared/modules/merge-request/components/item/MergeResultPipelineCard';
import MergePipelineCard from '@shared/modules/merge-request/components/item/MergePipelineCard';
import ACTION_ENUM from '@shared/modules/custom-roles/custom-roles.constants';
// eslint-disable-next-line max-len
import MergeRequestAutoMergeCard from '@shared/modules/merge-request-auto-merge/components/MergeRequestAutoMergeCard';
// eslint-disable-next-line max-len
import MergeRequestMergeAreaCard from '@shared/modules/merge-request/components/item/MergeRequestMergeAreaCard';
// eslint-disable-next-line max-len
import MergeRequestDiscussionReplyForm from '@shared/modules/merge-request/forms/item/MergeRequestDiscussionReplyForm';
import HelperService from '@shared/services/helper.service';
// eslint-disable-next-line max-len
import MergeRequestDiscussions from '@shared/modules/merge-request/components/item/MergeRequestDiscussions';
import { smallNotification } from '@shared/services/gf.notification.service';
import UserLabelItem from '@shared/shared-components/shared/user-label-item';
import RepoLabelList from '@shared/shared-components/shared/repo-label-list';
import GfCustomAlert from '@/shared-modules/shared-components/shared/GfCustomAlert';
import GfButton from '@/shared-modules/shared-components/shared/components/GfButton';
import MergeRequestMessages from '@/shared-modules/modules/merge-request/mergeRequestMessages';
import IssueCardShort from '@/shared-modules/modules/issue/components/IssueCardShort';

export default {
  name: 'PageMergeRequestOverview',
  components: {
    MergeResultPipelineCard,
    MergePipelineCard,
    MergeRequestAutoMergeCard,
    MergeRequestMergeAreaCard,
    GfButton,
    MergeRequestDiscussionReplyForm,
    MergeRequestDiscussions,
    GfCustomAlert,
    UserLabelItem,
    RepoLabelList,
    IssueCardShort
  },
  data() {
    return {
      areMergeRequestApproversModulesLoaded: false,
      areSecurityModulesLoaded: false,
      isNewDiscussionCreating: false
    };
  },
  computed: {
    alias() {
      return this.$route.params?.alias;
    },
    projectAlias() {
      return this.$route.params?.projectAlias;
    },
    mergeRequestId() {
      return this.$route.params?.id;
    },
    mergeRequest() {
      return this.$store.getters.getSelectedMergeRequest;
    },
    isMrApproversEnabled() {
      return this.$store.getters.getMrApprovers;
    },
    applicationVersion() {
      return this.$store.getters.getApplicationVersion;
    },
    ACTION_ENUM() {
      return ACTION_ENUM;
    },
    isMergeRequestResultPipelineShown() {
      return (
        (this.getActionAvailabilityFlag(ACTION_ENUM.READ_CI_CD) &&
          this.mergeRequest?.mergePipelineActionTypeSet?.includes('SHOW_KRS')) ||
        this.mergeRequest?.mergePipelineActionTypeSet?.includes('SHOW_KRS_INFO')
      );
    },
    isMergeRequestPipelineShown() {
      return (
        (this.getActionAvailabilityFlag(ACTION_ENUM.READ_CI_CD) &&
          this.mergeRequest?.mergePipelineActionTypeSet?.includes('SHOW_KS')) ||
        this.mergeRequest?.mergePipelineActionTypeSet?.includes('SHOW_KS_INFO')
      );
    },
    isMergeRequestAutoMergeShown() {
      return (
        (this.mergeRequest && this.mergeRequest?.autoMergeAllowed) ||
        (this.mergeRequest && this.mergeRequest?.autoMergeEnabled) ||
        (this.mergeRequest && this.mergeRequest?.autoMergeDisable)
      );
    },
    isMergeRequestMergeAreaShown() {
      return (
        (this.getActionAvailabilityFlag(ACTION_ENUM.EDIT_STATUS_OF_MERGE_REQUEST) ||
          this.getActionAvailabilityFlag(ACTION_ENUM.DO_MERGE_MERGE_REQUEST)) &&
        (this.mergeRequest?.status?.id === 'OPENED' || this.mergeRequest?.status?.id === 'TRAINS_CAR_CONFLICT')
      );
    },
    hasNeedToRebaseAlert() {
      return (
        this.mergeRequest.mergeStatus?.id === 'SOURCE_BRANCH_NEED_TO_REBASE' &&
        this.mergeRequest.conflicts?.length === 0 &&
        !this.mergeRequest.canConflictsBeResolvedInUI
      );
    },
    hasNeedToRebaseLocalAlert() {
      return (
        this.mergeRequest.mergeStatus?.id === 'SOURCE_BRANCH_NEED_LOCAL_REBASE' &&
        this.mergeRequest.conflicts?.length > 0
      );
    },
    areMergeRequestApproversEnabled() {
      return this.$store.getters.getMrApprovers;
    },
    hasEditAccess() {
      return document.getElementById('hasEditAccess')?.getAttribute('value') === 'true';
    },
    hasAccessToSecurityModule() {
      return this.getActionAvailabilityFlag(ACTION_ENUM.READ_VULNERABILITY);
    }
  },
  mounted() {
    this.loadApproversComponents();
    this.loadSecurityComponents();
    if (this.getActionAvailabilityFlag(ACTION_ENUM.READ_DISCUSSION)) {
      this.getMergeRequestDiscussions();
    }
  },
  methods: {
    getMergeRequest() {
      MergeRequestService.getMergeRequest(this.alias, this.projectAlias, this.mergeRequestId);
    },
    onApproveAction() {
      this.getMergeRequest();
    },
    getMergeRequestDiscussions() {
      MergeRequestService.getMergeRequestsDiscussions(this.alias, this.projectAlias, this.mergeRequestId).then(() => {
        if (this.$route.hash?.length > 0) {
          this.$refs.discussions.scrollIntoView({ block: 'start' });
        }
      });
    },
    onCreateDiscussionClick() {
      this.isNewDiscussionCreating = true;
      this.$nextTick(() => {
        this.$refs.createDiscussionForm.openEditor();
      });
    },
    onNewDiscussionFormClose() {
      this.isNewDiscussionCreating = false;
    },
    onNewDiscussionSubmit(data) {
      MergeRequestService.createDiscussion(
        this.alias,
        this.projectAlias,
        HelperService.generateDiscussionObject({ data }, this.mergeRequest)
      ).then(() => {
        this.getMergeRequest();
        this.isNewDiscussionCreating = false;
      });
    },
    rebaseSourceBranch() {
      MergeRequestService.rebaseSourceBranch(this.alias, this.projectAlias, this.mergeRequestId).then(() => {
        window.location.reload();
      });
    },
    cancelMergeRequest() {
      MergeRequestService.cancelMergeRequest(this.alias, this.projectAlias, this.mergeRequestId).then(() => {
        this.getMergeRequest();
        smallNotification('success', { title: 'Запрос на слияние отменен!' });
      });
    },
    closeMergeRequest() {
      MergeRequestService.closeMergeRequest(this.alias, this.projectAlias, this.mergeRequestId).then(() => {
        this.getMergeRequest();
        smallNotification('success', { title: 'Запрос на слияние закрыт!' });
      });
    },

    loadApproversComponents() {
      try {
        // eslint-disable-next-line max-len
        const mergeRequestApproveFormComponent = require('@shared/modules/merge-request-approver/components/MergeRequestApproveForm');
        Vue.component('MergeRequestApproveForm', (resolve) => {
          resolve(mergeRequestApproveFormComponent);
        });

        // eslint-disable-next-line max-len
        const mergeRequestApproveRulesComponent = require('@shared/modules/merge-request-approver/components/MergeRequestApproveRules');
        Vue.component('MergeRequestApproveRules', (resolve) => {
          resolve(mergeRequestApproveRulesComponent);
        });
        this.areMergeRequestApproversModulesLoaded = true;
      } catch (e) {
        console.warn(
          // eslint-disable-next-line max-len
          'Merge request approvers module is NOT included in your package, if you need it - please contact our support'
        );
      }
    },
    loadSecurityComponents() {
      try {
        // eslint-disable-next-line max-len
        const mergeRequestSecurityCardComponent = require('@shared/modules/security/components/MergeRequestSecurityCard');
        Vue.component('MergeRequestSecurityCard', (resolve) => {
          resolve(mergeRequestSecurityCardComponent);
        });
        this.areSecurityModulesLoaded = true;
      } catch (e) {
        console.warn(
          // eslint-disable-next-line max-len
          'Security module is NOT included in your package, if you need it - please contact our support'
        );
      }
    },
    getActionAvailabilityFlag(action) {
      return this.$store.getters.getActionAvailabilityFlag(action);
    }
  },
  i18n: {
    messages: MergeRequestMessages
  }
};
</script>

<style lang="scss">
$this: '.page-merge-request-overview';

#{$this} {
  display: flex;
  gap: 32px;

  @include theme.max-lg {
    flex-direction: column-reverse;
  }

  #{$this}__content {
    min-width: calc(100% - 322px);
    display: flex;
    flex-direction: column;
    gap: 24px;

    @include theme.max-xxl {
      min-width: calc(100% - 312px);
    }

    @include theme.max-xl {
      min-width: calc(100% - 252px);
    }

    @include theme.max-lg {
      min-width: 100%;
    }

    #{$this}__alert {
      width: 100%;

      & .gf-alert__content {
        width: 100%;
        align-items: normal;

        & .rebease-alert {
          display: flex;
          gap: 16px;
          align-items: center;
          justify-content: space-between;

          @include theme.max-sm {
            flex-direction: column;
            text-align: center;
          }

          & > span {
            width: min-content;
            flex-grow: 1;
          }

          & > * {
            @include theme.max-sm {
              width: 100% !important;
            }
          }
        }

        & .rebease-local-alert {
          text-align: center;
        }
      }
    }

    #{$this}__description {
      & > h6 {
        margin-bottom: 4px;
      }

      & > .description {
        padding: 16px;
        word-break: break-word;
        border: 1px solid var(--border-color);
        border-radius: 6px;
      }
    }

    #{$this}__discussions {
      display: flex;
      flex-direction: column;
      gap: 12px;
      scroll-margin-top: 60px;

      & > .title {
        min-height: 34px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;

        & > h5 {
          margin-bottom: 0;
        }
      }

      & > .content {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
    }
  }

  #{$this}__aside {
    min-width: 290px;
    display: flex;
    flex-direction: column;
    gap: 24px;

    @include theme.max-xxl {
      min-width: 280px;
    }

    @include theme.max-xl {
      min-width: 220px;
    }

    @include theme.max-lg {
      min-width: 100%;
      gap: 16px;
    }

    & > .form {
      display: flex;
      flex-direction: column;
      gap: 6px;

      & > .title {
        & > .lock-icon {
          margin-top: -5px;
          margin-right: 2px;
        }
      }

      & > .content {
        display: flex;
        flex-direction: column;
        gap: 8px;
        overflow-wrap: anywhere;

        &.--short-gap {
          gap: 4px;
        }

        & .muted {
          color: var(--text-muted);
        }

        & > .updated-at,
        & > .created-at {
          gap: 4px;
        }
      }

      & > .about {
        text-wrap-style: balance;
        font-size: 12px;

        & > a {
          text-decoration: underline;
        }
        & > .about-icon {
          display: inline-block !important;
          vertical-align: top;
          color: var(--primary);
        }
      }

      &.--assigned,
      &.--reviewers,
      &.--created {
        & > .content {
          @include theme.max-lg {
            display: grid;
            grid-template-columns: repeat(2, calc(50% - 4px));
          }
          @include theme.max-sm {
            display: flex;
          }
        }
      }
    }
  }
}
</style>
